import { Component, ChangeDetectionStrategy, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, PRIMARY_OUTLET, ParamMap, Router, RouterLink, UrlTree } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe, KeyValuePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription, filter, finalize, first, take } from 'rxjs';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';

import { InvoiceCreationService } from '@app/sales/invoice-creation/invoice-creation.service';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { LogisticService } from '@app/logistic/logistic.service';
import { CommonService } from '@app/common/common.service';
import { PartnerService } from '@app/common/partner/partner.service';
import { AlertService } from '@app/shared/alert.service';
import { ApiResponse, Partner, Station, Store, User } from '@app/shared/_models/common';
import { Delivery, Sales_invoice, lineNetTaxx, vatTotals } from '@app/shared/_models/sales';
import { SalesOrderState } from '@app/shared/constants-server';
import { DocType, EVENTTYP_DELETE, getDocTypeLabel, getOrderLabel, salesOrderColors, sales_order_mode } from '@app/shared/constants';
import { businessDayOf, pickedDateStr, yearsBefore } from '@app/shared/time';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { ActionBarComponent } from '@app/shared/action-bar/action-bar.component';
import { InvoiceCreationActionComponent } from '@app/shared/action-bar/invoice-creation-action/invoice-creation-action.component';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { SmartInputComponent } from '@app/shared/smart-input/smart-input.component';

// Mêmes règles de lecture d'URL que F4 (quotations.component.ts) : jour réel AAAA-MM-JJ, id entier positif sur 32 bits.
function urlDay(v: string | null): Date | null {
  return v && /^\d{4}-\d{2}-\d{2}$/.test(v) && dayjs(v).isValid() && dayjs(v).format('YYYY-MM-DD') === v ? dayjs(v).toDate() : null;
}

function urlId(v: string | null): string | null {
  return v !== null && /^\d{1,10}$/.test(v) && +v > 0 && +v <= 2147483647 ? String(+v) : null;
}

function pathOf(t: UrlTree): string {
  return t.root.children[PRIMARY_OUTLET]?.segments.map((s) => s.path).join('/') ?? '';
}

// « +02 » tronqué (cast ::text) complété : sinon la chaîne passerait pour une heure murale illisible.
function fullOffset(raw: any): any {
  return typeof raw === 'string' && /[+-]\d{2}$/.test(raw) ? raw + ':00' : raw;
}

function dayOf(raw: any): string | null {
  return raw ? businessDayOf(fullOffset(raw)) : null;
}

function esc(v: string): string {
  return v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

type Period = 'day' | 'month' | 'days30' | 'months12';

interface BlFilter { customer?: string; state: string; doc_type: string; store: string; user: string; datstart?: Date; datend?: Date; }

interface BlLine { product: number; product_name: string; qty: number; uprice_taxi: number; total_taxi: number; }

// Une ligne de la liste = un BL ; tout ce que le gabarit lit est calculé une fois au chargement, jamais à chaque détection.
interface BlRow {
  id: number;
  orderId: number;
  docType: string;
  state: string;
  color: string;
  stationId: number | null;
  userId: number | null;
  customerId: number | null;
  customerName: string;
  billAddr: string | null;            // null = adresse de facturation absente (V1 : carte en erreur)
  delivAddr: string;
  created: string | null;             // dates.created brut, pour le DatePipe
  createdDay: string | null;          // jour métier AAAA-MM-JJ, critère de Du/Au (même date que F4)
  validated: string | null;           // dates.preparation brut
  askdatdel: string | null;
  mode: number;
  modeLabel: string;
  signed: boolean;                    // realdatdel posé : BL signé, immuable, il ne s'annule plus
  lines: BlLine[];
  total_taxx: number;
  total_taxi: number;
}

// Un bloc de la liste = les BL affichés d'un même client ; dès 2 BL il s'affiche en groupe, qu'aucun tri ne sépare.
interface BlBlock { key: string; customerName: string; rows: BlRow[]; total_taxi: number; }

@Component({
  selector: 'app-invoice-creation-v2',
  templateUrl: './invoice-creation-v2.component.html',
  styleUrls: ['./invoice-creation-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuCommonComponent, ActionBarComponent, InvoiceCreationActionComponent, SearchPartnerComponent, SelectDateComponent, SmartInputComponent, RouterLink, FormsModule, NgClass, NgTemplateOutlet, KeyValuePipe, DatePipe, DecimalPipe, MatButtonModule, MatIconModule, MatTooltipModule, MatCheckboxModule, MatProgressSpinnerModule],
})
export class InvoiceCreationV2Component implements OnInit {
  private invoiceCreationService = inject(InvoiceCreationService);
  private quotationService = inject(QuotationService);
  private logisticService = inject(LogisticService);
  private commonService = inject(CommonService);
  private partnerService = inject(PartnerService);
  private alertService = inject(AlertService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  public SalesOrderState = SalesOrderState;
  // Sans « Location » : la requête écarte les BL des contrats de location (R-LOC), ce choix ne montrerait jamais rien.
  public readonly docTypes: DocType[] = Object.values(DocType).filter((t) => t !== DocType.LOCATION);
  public getLabel = getOrderLabel;
  public docTypeName = getDocTypeLabel;

  public loading = true;
  public loadFailed = false;
  public loadedAt: Date | null = null;       // heure de la lecture : les filtres trient cette lecture, ils ne rappellent pas le serveur
  public rows: BlRow[] = [];                 // tous les BL à facturer renvoyés par le serveur
  public visible: BlRow[] = [];              // ceux que les filtres laissent voir, dans l'ordre de tri
  public blocks: BlBlock[] = [];             // les mêmes, rangés par client : ce que le gabarit parcourt
  public cancelingId: number | null = null;  // BL dont l'annulation est en cours : un seul à la fois
  public hovered: BlRow | null = null;       // BL dont le panneau de droite montre les articles
  public selectedIds = new Set<number>();
  private selVersion = 0;                    // change à chaque modification de la sélection : clé du cache des totaux

  public stores: Store[] = [{ id: -1, name: 'Tous' }];
  public users: User[] = [{ id: -1, store: -1, currentstore: 0, firstname: 'Tous' }];
  private stationStore = new Map<number, number>();
  private storesLoaded = false;
  private usersLoaded = false;

  public displayedColumnsInfo: [string, string, string, boolean?][] = [
    ['sel', '', 'size0'],
    ['id', 'BL', 'size1'],
    ['order', 'Cmd', 'size1'],
    ['created', 'Cmd créée', 'size2'],
    ['delivery', 'Livraison', 'size2'],
    ['type', 'Type', 'size0'],
    ['store', 'E', 'size1'],
    ['station', 'P', 'size1'],
    ['user', 'Emp.', 'size1'],
    ['status', 'Statut', 'size1'],
    ['customer', 'Client', 'size2'],
    ['bill', 'Adresse de facturation', 'size2'],
    ['lines', 'Lig.', 'size0'],
    ['total', 'Total TTC', 'size1'],
    ['cancel', '', 'size0'],
  ];

  // Arrivée sur l'écran : les 30 derniers jours glissants (demande de JP), sans client.
  private defaultFilter(): BlFilter {
    const [s, e] = this.periodRange('days30');
    return { state: '-1', doc_type: '-1', store: '-1', user: '-1', datstart: s, datend: e };
  }
  public filter: BlFilter = this.defaultFilter();
  public customer: Partner | null = null;

  @ViewChild('orderIdNav') orderIdNavInput?: SmartInputComponent;
  public orderIdText = '';
  public orderIdHref: string | null = null;
  public orderId: string | null = null;      // id de commande dont la liste montre seuls les BL (raccourci), null hors raccourci

  private alive = true;
  private started = false;                   // l'URL ne s'écrit qu'une fois la navigation d'arrivée terminée (cf. F4)
  private ownWrites = 0;
  private syncPending = false;
  private loadSub?: Subscription;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.alive = false);
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((q) => this.applyUrl(q));
    this.commonService.getStores().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (stores: Store[]) => { this.stores = [...this.stores, ...stores]; this.storesLoaded = true; this.reconcile(); } });
    this.commonService.getStations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (stations: Station[]) => { for (const s of stations ?? []) if (s.id != null && s.store != null) this.stationStore.set(s.id, s.store); this.applyFilters(); } });
    this.commonService.getUsers().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (users: User[]) => { this.users = [...this.users, ...users]; this.usersLoaded = true; this.reconcile(); } });
    this.loadData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => { if (!this.alive) return; this.started = true; this.syncUrl(); });
    const idField = this.orderIdNavInput?.inputElement?.nativeElement;
    if (idField) idField.tabIndex = 1;
  }

  // --- Chargement : une seule requête, articles compris ; les filtres s'appliquent ensuite dans le navigateur ---

  loadData(): void {
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadFailed = false;
    this.loadSub = this.invoiceCreationService.getDeliveriesToInvoice()
      .pipe(first(), finalize(() => { this.loading = false; }))
      .subscribe({
        next: (data: Sales_invoice[]) => {
          this.rows = this.buildRows(data || []);
          this.loadedAt = new Date();
          this.hovered = null;
          this.selectedIds.clear();
          this.applyFilters();
        },
        error: (e) => {
          this.loadFailed = true;
          const serverMsg = e.error?.message || e.error || e.message || 'Erreur inconnue';
          Swal.fire({ title: 'Erreur', text: `Erreur au chargement des commandes à facturer.\nDétails: ${serverMsg}`, icon: 'error', confirmButtonText: 'Fermer' });
          console.error(e);
        }
      });
  }

  private buildRows(groups: Sales_invoice[]): BlRow[] {
    const rows: BlRow[] = [];
    for (const g of groups) {
      const bill = g.lnk_addr_bill;
      const billAddr = bill && bill.id !== 0 ? [bill.street1, bill.zip, bill.city].filter(Boolean).join(' ') : null;
      const customerName = [g.lnk_customer?.name, g.lnk_customer?.firstname].filter(Boolean).join(' ');
      for (const d of g.lnk_deliveries ?? []) {
        const o: any = d.lnk_order ?? {};
        const deliv = o.lnk_partner_deliv;
        const { lines, total_taxx, total_taxi } = this.deliveryLines(d);
        rows.push({
          id: d.id,
          orderId: d.id_order,
          docType: o.doc_type ?? '',
          state: o.state ?? '',
          color: salesOrderColors[o.state as SalesOrderState] ?? '',
          stationId: o.station ?? null,
          userId: o.id_user ?? null,
          customerId: g.customer ?? null,
          customerName,
          billAddr,
          delivAddr: deliv ? [deliv.street1, deliv.zip, deliv.city].filter(Boolean).join(' ') : '',
          created: o.dates?.created ?? null,
          createdDay: dayOf(o.dates?.created),
          validated: o.dates?.preparation ?? null,
          askdatdel: d.askdatdel ?? null,
          mode: d.mode,
          modeLabel: sales_order_mode.get(d.mode) ?? 'Mode ' + d.mode,
          signed: !!d.realdatdel,
          lines,
          total_taxx,
          total_taxi,
        });
      }
    }
    return rows;
  }

  // Mêmes calculs que la V1 (InvoiceCreationComponent.getDeliveryLines / getDeliveryTotal) : les deux écrans affichent les mêmes montants.
  private deliveryLines(d: Delivery): { lines: BlLine[]; total_taxx: number; total_taxi: number } {
    const orderLines: any[] = (d.lnk_order as any)?.lines ?? [];
    const lines: BlLine[] = [];
    const bases: { vat_rate: any; net_taxx: number }[] = [];
    for (const dl of (d.lnk_deliveryl ?? []) as any[]) {
      const ol = orderLines.find((l: any) => l.id === dl.id_order_l);
      const unet = ol ? +(ol.unet_taxi ?? ol.uprice_taxi) : 0;
      lines.push({ product: ol?.product ?? 0, product_name: ol ? ol.product_name : 'Produit inconnu', qty: +dl.qty, uprice_taxi: unet, total_taxi: +dl.qty * unet });
      if (ol && +dl.qty > 0) bases.push({ vat_rate: ol.vat_rate, net_taxx: lineNetTaxx(+dl.qty, +(ol.unet_taxx ?? ol.uprice_taxx)) });
    }
    const t = vatTotals(bases);
    return { lines, total_taxx: t.amount_taxx, total_taxi: t.amount_taxi };
  }

  // --- Filtres (tous appliqués ici : GET /sales/deliveriestoinvoice ne prend aucun paramètre) ---

  storeOf(r: BlRow): number | null {
    return r.stationId != null ? this.stationStore.get(r.stationId) ?? null : null;
  }

  private matches(r: BlRow): boolean {
    if (this.orderId) return String(r.orderId) === this.orderId;
    const f = this.filter;
    if (f.customer && String(r.customerId) !== f.customer) return false;
    if (f.state !== '-1' && r.state !== f.state) return false;
    if (f.doc_type !== '-1' && r.docType !== f.doc_type) return false;
    if (f.store !== '-1' && String(this.storeOf(r)) !== f.store) return false;
    if (f.user !== '-1' && String(r.userId) !== f.user) return false;
    const from = pickedDateStr(f.datstart), to = pickedDateStr(f.datend);
    if ((from || to) && !r.createdDay) return false;
    if (from && r.createdDay! < from) return false;
    if (to && r.createdDay! > to) return false;
    return true;
  }

  // Un BL qui sort de la vue sort aussi de la sélection : on ne facture jamais un BL qu'on ne voit pas.
  applyFilters(): void {
    this.arrange(this.rows.filter((r) => this.matches(r)));
    const ids = new Set(this.visible.map((r) => r.id));
    for (const id of [...this.selectedIds]) if (!ids.has(id)) this.selectedIds.delete(id);
    this.selVersion++;
    if (this.hovered && !ids.has(this.hovered.id)) this.hovered = null;
  }

  get hiddenCount(): number { return this.rows.length - this.visible.length; }

  private onFilterChange(): void {
    this.orderId = null;
    this.applyFilters();
    this.syncUrl();
  }

  onSelectChange(): void { this.onFilterChange(); }

  customerChange(customer: any): void {
    if (customer && customer.id && customer.name) {
      this.filter.customer = customer.id.toString();
      this.customer = customer;
      // Client choisi = tous ses BL à facturer : la route n'a pas de borne de date, les 5 ans de F4 (borne de charge serveur) n'auraient pas de sens ici.
      this.filter.datstart = undefined;
      this.filter.datend = new Date();
    } else {
      this.customer = null;
      delete this.filter.customer;
    }
    this.onFilterChange();
  }

  dateChange(date: any, type: 'datstart' | 'datend'): void {
    this.filter[type] = date ?? undefined;
    this.onFilterChange();
  }

  public readonly periods: { key: Period; label: string; title: string }[] = [
    { key: 'day', label: 'Jour', title: "Aujourd'hui" },
    { key: 'month', label: 'Mois', title: "Mois en cours : du 1er à aujourd'hui" },
    { key: 'days30', label: '30 j', title: "30 derniers jours, aujourd'hui compris" },
    { key: 'months12', label: '12 m', title: "12 derniers mois : du lendemain de la même date l'an dernier à aujourd'hui" },
  ];

  private periodRange(key: Period): [Date, Date] {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
    if (key === 'month') return [new Date(y, m, 1), today];
    if (key === 'days30') return [new Date(y, m, d - 29), today];
    if (key === 'months12') { const s = yearsBefore(today, 1); s.setDate(s.getDate() + 1); return [s, today]; }
    return [new Date(y, m, d), today];
  }

  isPeriod(key: Period): boolean {
    const [s, e] = this.periodRange(key);
    return pickedDateStr(this.filter.datstart) === pickedDateStr(s) && pickedDateStr(this.filter.datend) === pickedDateStr(e);
  }

  setPeriod(key: Period): void {
    [this.filter.datstart, this.filter.datend] = this.periodRange(key);
    this.onFilterChange();
  }

  // Effacer : tous les BL à facturer (« Du » vide, « Au » aujourd'hui, comme Réinitialiser de F4).
  onCancel(): void {
    this.filter = { ...this.defaultFilter(), datstart: undefined, datend: new Date() };
    this.customer = null;
    this.orderId = null;
    this.showOrderId(null);
    this.applyFilters();
    this.syncUrl();
  }

  // --- « Id commande » : Tab ou Entrée ne montre que les BL de cette commande, l'icône ouvre la commande ---

  onOrderIdChange(value: string): void {
    const id = urlId(String(value ?? '').trim());
    this.orderIdHref = id ? this.router.serializeUrl(this.router.createUrlTree(['/sales/quotation', +id])) : null;
  }

  onOrderIdBlur(value: string): void {
    if (!this.alive || this.router.currentNavigation()) return;
    const id = urlId(String(value ?? '').trim());
    if (id === this.orderId) return;
    if (id) this.enterOrderId(id); else this.onFilterChange();
  }

  onOrderIdValidate(value: string): void {
    const id = urlId(String(value ?? '').trim());
    if (id) this.enterOrderId(id);
  }

  keepOrderIdFocus(e: MouseEvent): void {
    if (e.button === 0 && this.orderIdNavInput?.isFocused()) e.preventDefault();
  }

  // La barre repasse à l'état d'Effacer : l'écran ne montre aucun critère que le raccourci ignore.
  private enterOrderId(id: string): void {
    this.filter = { ...this.defaultFilter(), datstart: undefined, datend: new Date() };
    this.customer = null;
    this.orderId = id;
    this.showOrderId(id);
    this.applyFilters();
    this.syncUrl();
  }

  private showOrderId(id: string | null): void {
    this.orderIdText = id ?? '';
    this.onOrderIdChange(this.orderIdText);
  }

  // --- URL = source des filtres, comme F4 : un lien copié ou un Retour rend la même vue ---

  private applyUrl(q: ParamMap): void {
    if (this.ownWrites) return;
    this.readUrl(q);
    this.normalize();
    this.applyFilters();
    this.syncUrl();
  }

  private readUrl(q: ParamMap): void {
    const id = urlId(q.get('id'));
    if (id) { this.filter = { ...this.defaultFilter(), datstart: undefined, datend: new Date() }; this.customer = null; this.orderId = id; this.showOrderId(id); return; }
    const o = this.defaultFilter();
    const day = (k: string, dflt?: Date) => q.get(k) === '' ? undefined : urlDay(q.get(k)) ?? dflt;
    const pick = (k: string, allowed: string[], dflt: string) => allowed.includes(q.get(k) ?? '') ? q.get(k)! : dflt;
    const customer = urlId(q.get('customer'));
    this.filter = {
      state: pick('state', Object.values(SalesOrderState), o.state),
      doc_type: pick('type', this.docTypes, o.doc_type),
      store: urlId(q.get('store')) ?? o.store,
      user: urlId(q.get('user')) ?? o.user,
      datstart: day('from', o.datstart),
      datend: day('to', o.datend),
    };
    if (customer) this.filter.customer = customer;
    this.showCustomer(customer);
    this.orderId = null;
    this.showOrderId(null);
  }

  // Nom du client de l'URL : pris dans les BL chargés, sinon relu ; inconnu du serveur, le filtre tombe.
  private showCustomer(id: string | null): void {
    if (id === null) { this.customer = null; return; }
    if (this.customer && String(this.customer.id) === id) return;
    this.customer = null;
    this.partnerService.getPartnerByid(+id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (partners: Partner[]) => {
        if (this.filter.customer !== id) return;
        if (partners?.[0]?.id) { this.customer = partners[0]; return; }
        delete this.filter.customer;
        this.applyFilters();
        this.syncUrl();
      },
      error: (e) => {
        console.error('[InvoiceCreationV2Component] client du filtre illisible', e);
        if (this.filter.customer === id) this.customer = { id: +id, name: 'Client n° ' + id } as Partner;
      },
    });
  }

  private normalize(): void {
    const known = (list: { id?: number }[], v?: string) => !v || v === '-1' || list.some((x) => String(x.id) === v);
    if (this.storesLoaded && !known(this.stores, this.filter.store)) this.filter.store = '-1';
    if (this.usersLoaded && !known(this.users, this.filter.user)) this.filter.user = '-1';
  }

  private reconcile(): void {
    const before = JSON.stringify(this.queryOf());
    this.normalize();
    if (JSON.stringify(this.queryOf()) !== before) { this.applyFilters(); this.syncUrl(); }
  }

  private queryOf(): Record<string, string> {
    if (this.orderId) return { id: this.orderId };
    const f = this.filter, q: Record<string, string> = {};
    if (f.customer) q['customer'] = f.customer;
    if (f.state !== '-1') q['state'] = f.state;
    if (f.doc_type !== '-1') q['type'] = f.doc_type;
    if (f.store !== '-1') q['store'] = f.store;
    if (f.user !== '-1') q['user'] = f.user;
    q['from'] = pickedDateStr(f.datstart) ?? '';
    q['to'] = pickedDateStr(f.datend) ?? '';
    return q;
  }

  // replaceUrl : aucun filtre n'ajoute d'entrée d'historique ; pendant une navigation vers une autre page, on réécrit après sa fin (cf. F4).
  private syncUrl(): void {
    if (!this.alive || !this.started || this.syncPending) return;
    const nav = this.router.currentNavigation();
    if (nav && pathOf(nav.extractedUrl) !== pathOf(this.router.createUrlTree([]))) {
      this.syncPending = true;
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError), take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => setTimeout(() => { this.syncPending = false; this.syncUrl(); }));
      return;
    }
    const queryParams = this.queryOf();
    const cur = this.route.snapshot.queryParamMap;
    const keys = Object.keys(queryParams);
    if (cur.keys.length === keys.length && keys.every((k) => cur.get(k) === queryParams[k])) return;
    this.ownWrites++;
    this.router.navigate([], { queryParams, replaceUrl: true })
      .catch((e) => console.error('[InvoiceCreationV2Component] URL des filtres non écrite', e))
      .finally(() => this.ownWrites--);
  }

  // --- Tri par entête, comme F4 ; ordre d'arrivée = celui du serveur (client puis adresse de facturation, puis n° de BL) ---
  // Les BL d'un client ne se séparent jamais : le groupe se range sur son BL de tête, le plus petit en croissant, le plus grand en décroissant.

  private sortKey: string | null = null;
  private sortAsc = true;
  private readonly STATE_RANK: string[] = Object.values(SalesOrderState);
  private readonly DOCTYPE_RANK: string[] = Object.values(DocType);

  sortBy(name: string): void {
    if (name === 'sel' || name === 'cancel') return;
    this.sortAsc = this.sortKey === name ? !this.sortAsc : true;
    this.sortKey = name;
    this.displayedColumnsInfo = this.displayedColumnsInfo.map((c) => c[0] === name ? [c[0], c[1], c[2], this.sortAsc] : [c[0], c[1], c[2]]);
    this.arrange(this.visible);
  }

  private arrange(list: BlRow[]): void {
    const byCustomer = new Map<string, BlRow[]>();
    for (const r of list) {
      const key = r.customerId != null ? 'c' + r.customerId : 'b' + r.id;
      const rows = byCustomer.get(key);
      if (rows) rows.push(r); else byCustomer.set(key, [r]);
    }
    const blocks: BlBlock[] = [...byCustomer].map(([key, rows]) => ({ key, rows, customerName: rows[0].customerName, total_taxi: Math.round(rows.reduce((t, r) => t + r.total_taxi, 0) * 100) / 100 }));
    if (this.sortKey) {
      const k = this.sortKey, dir = this.sortAsc ? 1 : -1;
      const cmp = (a: BlRow, b: BlRow) => {
        const va = this.sortValue(a, k), vb = this.sortValue(b, k);
        return (typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb))) * dir;
      };
      for (const b of blocks) b.rows.sort((a, c) => cmp(a, c) || a.id - c.id);
      blocks.sort((a, b) => cmp(a.rows[0], b.rows[0]) || a.customerName.localeCompare(b.customerName) || a.rows[0].id - b.rows[0].id);
    }
    this.blocks = blocks;
    this.visible = blocks.flatMap((b) => b.rows);
  }

  private sortValue(r: BlRow, k: string): number | string {
    switch (k) {
      case 'id': return r.id;
      case 'order': return r.orderId;
      case 'created': return r.created ? Date.parse(fullOffset(r.created)) || 0 : 0;
      case 'delivery': return r.askdatdel ?? '';
      case 'type': { const i = this.DOCTYPE_RANK.indexOf(r.docType); return i === -1 ? this.DOCTYPE_RANK.length : i; }
      case 'store': return this.storeOf(r) ?? 0;
      case 'station': return r.stationId ?? 0;
      case 'user': return this.userShortName(r.userId);
      case 'status': { const i = this.STATE_RANK.indexOf(r.state); return i === -1 ? this.STATE_RANK.length : i; }
      case 'customer': return r.customerName;
      case 'bill': return r.billAddr ?? '';
      case 'lines': return r.lines.length;
      case 'total': return r.total_taxi;
      default: return '';
    }
  }

  userShortName(id: number | null): string {
    const u = this.users.find((x) => x.id === id && id !== -1);
    return u ? ((u.firstname ?? '') + ' ' + (u.lastname?.[0] ?? '')).trim() : '';
  }

  docTypeLabel(r: BlRow): string { return r.docType ? getDocTypeLabel(r.docType) : ''; }

  // --- Sélection : bornée aux BL visibles ---

  toggle(r: BlRow, checked?: boolean): void {
    const on = checked ?? !this.selectedIds.has(r.id);
    if (on) this.selectedIds.add(r.id); else this.selectedIds.delete(r.id);
    this.selVersion++;
  }

  selectAll(): void { for (const r of this.visible) this.selectedIds.add(r.id); this.selVersion++; }

  deselectAll(): void { this.selectedIds.clear(); this.selVersion++; }

  get allVisibleSelected(): boolean { return this.visible.length > 0 && this.visible.every((r) => this.selectedIds.has(r.id)); }

  toggleAllVisible(checked: boolean): void { if (checked) this.selectAll(); else this.deselectAll(); }

  // Mémoïsés sur la référence de `visible` et la version de la sélection : le gabarit les lit à chaque détection.
  private kpiSource: BlRow[] | null = null;
  private kpiVersion = -1;
  private kpiCache = { visible: 0, selected: 0 };
  private kpi(): { visible: number; selected: number } {
    if (this.kpiSource !== this.visible || this.kpiVersion !== this.selVersion) {
      let v = 0, s = 0;
      for (const r of this.visible) { v += r.total_taxi; if (this.selectedIds.has(r.id)) s += r.total_taxi; }
      this.kpiCache = { visible: Math.round(v * 100) / 100, selected: Math.round(s * 100) / 100 };
      this.kpiSource = this.visible;
      this.kpiVersion = this.selVersion;
    }
    return this.kpiCache;
  }
  get totalVisible(): number { return this.kpi().visible; }
  get totalSelected(): number { return this.kpi().selected; }

  onHover(r: BlRow): void { this.hovered = r; }

  // --- « Annuler le BL » = la suppression de l'écran de livraison (event 3) : le serveur refuse un BL signé ou facturé ---

  cancelBl(r: BlRow): void {
    if (r.signed || this.cancelingId !== null) return;
    const total = r.total_taxi.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    Swal.fire({
      title: `Annuler le BL N°${r.id} ?`,
      html: `Commande N°${r.orderId} · ${esc(r.customerName)} · <b>${total}&nbsp;€ TTC</b><br><small>Le BL est supprimé : ses quantités redeviennent à livrer sur la commande, qui reste ouverte.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Annuler le BL',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Garder le BL',
    }).then((res) => { if (res.isConfirmed) this.postCancel(r); });
  }

  private postCancel(r: BlRow): void {
    this.cancelingId = r.id;
    const fg = Delivery.asFormGroup(new Delivery(r.id, r.orderId, 0, r.mode));
    this.logisticService.postDeliveryEvent(EVENTTYP_DELETE, fg).pipe(first(), finalize(() => { this.cancelingId = null; })).subscribe({
      next: (response: ApiResponse) => {
        if (response?.success === false) {
          Swal.fire({ title: 'BL non annulé', text: response.msg || 'Le serveur a refusé la suppression du BL.', icon: 'error', confirmButtonText: 'Fermer' });
          return;
        }
        this.rows = this.rows.filter((x) => x.id !== r.id);
        this.selectedIds.delete(r.id);
        this.applyFilters();
        this.alertService.success(`BL N°${r.id} annulé : ses quantités redeviennent à livrer sur la commande N°${r.orderId}.`);
      },
      error: (e) => {
        const serverMsg = e.error?.msg || e.error?.message || e.error || e.message || 'Erreur inconnue';
        Swal.fire({ title: 'Erreur', text: `Le BL N°${r.id} n'a pas été annulé.\nDétails: ${serverMsg}`, icon: 'error', confirmButtonText: 'Fermer' });
        console.error(e);
      }
    });
  }

  // --- Facturation : même event 101 que la V1, précédé d'un récapitulatif (le scellement est irréversible) ---

  onActionBarAction(action: string): void {
    if (action !== 'INVOICING') return;
    const ids = this.visible.filter((r) => this.selectedIds.has(r.id)).map((r) => r.id);
    if (!ids.length) { this.alertService.warn('Aucun bon de livraison sélectionné.'); return; }
    const total = this.totalSelected.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    Swal.fire({
      title: 'Facturer',
      html: `Émettre <b>${ids.length}</b> facture${ids.length > 1 ? 's' : ''} pour <b>${total}&nbsp;€ TTC</b> ?<br><small>Une facture par BL, scellée : l'opération ne s'annule que par un avoir.</small>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Facturer',
      cancelButtonText: 'Annuler',
    }).then((r) => { if (r.isConfirmed) this.postInvoicing(new Set(ids)); });
  }

  private postInvoicing(ids: Set<number>): void {
    this.quotationService.postInvoicingEvent(ids).pipe(first()).subscribe({
      next: (response: ApiResponse) => {
        if (response?.data?.failed_invoices?.length > 0) {
          const errorMessages = response.data.failed_invoices.map((fi: any) => `<li>BL <b>${fi.delivery_id}</b> : ${fi.error}</li>`).join('');
          Swal.fire({ title: 'Attention', html: `L'opération a été traitée avec des erreurs :<br><ul style="text-align: left; max-height: 200px; overflow-y: auto;">${errorMessages}</ul>`, icon: 'warning', confirmButtonText: 'OK' });
        } else {
          this.alertService.success('Opération de facturation traitée avec succès.');
        }
        this.loadData();
      },
      error: (e) => {
        const serverMsg = e.error?.message || e.error || e.message || 'Erreur inconnue';
        Swal.fire({ title: 'Erreur', text: `L'opération de facturation a échoué.\nDétails: ${serverMsg}`, icon: 'error', confirmButtonText: 'Fermer' });
        console.error(e);
      }
    });
  }
}
